// deps
import React, { ReactElement, memo, SVGAttributes } from "react";

// hooks
import { useCountdown } from "@payment-ui/hooks/useCountdown";

// styles
import S from "./TimerWidget.scss";

// helpers
import { getTimeInMins } from "@payment-ui/helpers/Upi";

// constants
import { TIMER_DEFAULT_PROPS_V4 } from "@payment-ui/constants/Upi";

// test-ids
import { TIMER_WIDGET_TEST_IDS } from "@swiggy-private/payment-test-ids";

const GRAY_SLATE_COLOR_CODE = "#F0F0F5";
interface TimerWidgetProps {
    isPlaying: boolean;
    duration: number;
    colors: { colorCode: string; duration: number }[];
    size?: number;
    trailColor?: string;
    strokeWidth?: number;
    strokeLinecap?: SVGAttributes<SVGImageElement>["strokeLinecap"];
    startAt?: number;
}

interface StrokesProps {
    center: number;
    offset: number;
    pathLength: number;
    stroke: string;
}

const getWrapperStyle = (size: number): object => ({
    position: "relative",
    width: size,
    height: size,
    borderRadius: size / 2,
});

function Strokes(props: StrokesProps): ReactElement {
    const { center, offset, pathLength, stroke } = props;
    const strokeOffset =
        TIMER_DEFAULT_PROPS_V4.NUMBER_OF_STROKES -
        Math.floor((offset / pathLength) * TIMER_DEFAULT_PROPS_V4.NUMBER_OF_STROKES);
    return (
        <>
            {[...Array(TIMER_DEFAULT_PROPS_V4.NUMBER_OF_STROKES)].map((_, i) => {
                const _transform = `rotate(${(i * 360) /
                    TIMER_DEFAULT_PROPS_V4.NUMBER_OF_STROKES} ${center} ${center})`;
                const color = i >= strokeOffset ? GRAY_SLATE_COLOR_CODE : stroke;
                return (
                    <>
                        {i % (TIMER_DEFAULT_PROPS_V4.NUMBER_OF_STROKES / 4) ? (
                            <rect x={center} y="2" key={i} width="1" height="9" fill={color} transform={_transform} />
                        ) : (
                            <rect x={center} y="2" key={i} width="1" height="13" fill={color} transform={_transform} />
                        )}
                    </>
                );
            })}
        </>
    );
}

function TimerWidget(props: TimerWidgetProps): ReactElement {
    const {
        duration,
        colors,
        size = TIMER_DEFAULT_PROPS_V4.SIZE,
        strokeLinecap = TIMER_DEFAULT_PROPS_V4.STROKELIKE_CAP,
        strokeWidth = TIMER_DEFAULT_PROPS_V4.STROKE_WIDTH,
        startAt,
    } = props;
    const { path, pathLength, stroke, strokeDashoffset, timeProps } = useCountdown({
        duration,
        colors,
        rotation: TIMER_DEFAULT_PROPS_V4.ROTATION,
        size,
        strokeWidth,
        startAt,
    });
    return (
        <div className={S.outerContainer}>
            <div
                style={getWrapperStyle(TIMER_DEFAULT_PROPS_V4.CONTAINER_SIZE)}
                data-testid={TIMER_WIDGET_TEST_IDS.CONTAINER}
                className={S.container}
            >
                <svg width={size} height={size} fill="none">
                    <Strokes offset={strokeDashoffset} pathLength={pathLength} center={size / 2} stroke={stroke} />
                    <path d={path} fill="none" strokeWidth={strokeWidth} />
                    <path
                        d={path}
                        fill="none"
                        stroke={stroke}
                        strokeLinecap={strokeLinecap}
                        strokeWidth={strokeWidth}
                        strokeDasharray={pathLength}
                        strokeDashoffset={strokeDashoffset}
                    />
                </svg>

                <div className={S.timer}>
                    <div className={S.timerTextContainer}>
                        <div className={S.timerTime} data-testid={TIMER_WIDGET_TEST_IDS.REMAINING_TIME}>
                            {timeProps.remainingTime < 60
                                ? timeProps.remainingTime
                                : getTimeInMins(timeProps.remainingTime)}
                        </div>
                        <div className={S.timerText}> {timeProps.remainingTime < 60 ? "secs" : "mins"} remaining</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default memo(TimerWidget);
